/**
 * The words the app uses for the things it manages.
 *
 * One set of words for every organisation (October 2026): rows, streams,
 * routes, data buyers and data sellers. There used to be a second, lead
 * distribution set chosen per organisation; it is gone, and the stored
 * `vocabulary` setting no longer changes anything on screen.
 *
 * Display only. These words change what the screen says, never a path, a
 * table, a JSON field, a role key or a nav tab key: `/api/v1/leads`,
 * `campaign_id` and the `buyer` role stay exactly as they are.
 *
 * Every form is written out. Nothing here, and nothing that reads it, makes a
 * plural by adding an "s" or a capital by upper-casing a letter: "an endpoint"
 * and "a row" are why. A sentence with a term in it is a template that slots
 * in one of these forms whole.
 *
 * Email campaigns are not in here and must never go through it. They are the
 * customer's own mailings, called campaigns; the `campaign` noun below is the
 * routing campaign (a stream) only.
 */

export type Noun =
  | 'lead' | 'buyer' | 'supplier' | 'campaign' | 'destination' | 'routeGroup' | 'route'
  | 'company' | 'deal' | 'pipeline' | 'stage'
  | 'task' | 'note' | 'call' | 'meeting' | 'activity'
  | 'person' | 'team' | 'owner' | 'property'
  | 'workflow' | 'run';

export type Forms = {
  /** "row" */
  one: string;
  /** "rows" */
  many: string;
  /** "Row" */
  One: string;
  /** "Rows" */
  Many: string;
  /** "a row", "an endpoint" */
  a: string;
  /** "A row", "An endpoint" */
  A: string;
};

/**
 * Words that do not compose from the nouns, or are not nouns at all.
 *
 * `sourceTag` is the lead's existing `source` field (bulk_import, a supplier's
 * feed name). It is called a source tag so "source" never reads as the data
 * seller a row came from.
 *
 * `url` and friends label a destination's address fields. A destination is
 * an endpoint, so its address is a URL rather than "Endpoint endpoint".
 */
export type Phrases = {
  /** The nav group and the process: "Routing". */
  routing: string;
  /** Lower case, for a sentence: "routing". */
  routingLower: string;
  /** The reports page: "Analytics". */
  reports: string;
  sourceTag: string;
  sourceTagLower: string;
  sourceTagMany: string;
  sourceTagManyLower: string;
  /** "sold". Money is called revenue. */
  sold: string;
  Sold: string;
  /** Simple past, "It ___ for £11": "sold". */
  soldPast: string;
  /** After has/have: "has already ___": "sold". */
  soldPerfect: string;
  /** The word between sold and the buyer: "to". */
  soldPrep: string;
  /** "Unsold". */
  Unsold: string;
  /** "Value per row". */
  leadValue: string;
  /** Lower case, for a sentence: "value per row". */
  leadValueLower: string;
  /** The sum of every row's value: "total value". */
  totalLeadValue: string;
  /** The email-and-phone column: "Email and phone". */
  contactDetails: string;
  /** How a buyer login and a supplier login are described. Keys unchanged. */
  roleBuyer: string;
  roleSupplier: string;
  /** How a sales login is described: "Sales". */
  roleSales: string;
  /**
   * A company's roles: what a company with a buyer record is ("Data buyer")
   * and with a supplier record ("Data seller"). The Buyers and Suppliers
   * menu items read the plurals. The role keys
   * (`data_buyer`, `data_seller`) never change.
   */
  dataBuyer: string;
  dataBuyers: string;
  dataBuyerLower: string;
  dataSeller: string;
  dataSellers: string;
  dataSellerLower: string;
  /** A destination's address: "URL". */
  url: string;
  /** In a sentence: "URL". */
  urlLower: string;
  pingUrl: string;
  postUrl: string;
};

export type Vocabulary = Record<Noun, Forms> & { phrases: Phrases };

export const TERMS: Vocabulary = {
  // Everything that arrives is a row, whatever its type (lead, order,
  // booking...); the human a row is about is a person (the `person` noun
  // below). Display only: the key stays `lead`.
  lead: { one: 'row', many: 'rows', One: 'Row', Many: 'Rows', a: 'a row', A: 'A row' },
  // A buyer or supplier record is a company's data buyer or data seller
  // role, so the nouns read as the role (One Rowstorm, step 1).
  buyer: {
    one: 'data buyer', many: 'data buyers', One: 'Data buyer', Many: 'Data buyers',
    a: 'a data buyer', A: 'A data buyer',
  },
  supplier: {
    one: 'data seller', many: 'data sellers', One: 'Data seller', Many: 'Data sellers',
    a: 'a data seller', A: 'A data seller',
  },
  campaign: { one: 'stream', many: 'streams', One: 'Stream', Many: 'Streams', a: 'a stream', A: 'A stream' },
  destination: {
    one: 'endpoint', many: 'endpoints', One: 'Endpoint', Many: 'Endpoints', a: 'an endpoint', A: 'An endpoint',
  },
  routeGroup: { one: 'tier', many: 'tiers', One: 'Tier', Many: 'Tiers', a: 'a tier', A: 'A tier' },
  // Where a row goes: one tier's link to one destination, whether it sells,
  // assigns to a rep or sends to a webhook. The Routes page lists them.
  route: { one: 'route', many: 'routes', One: 'Route', Many: 'Routes', a: 'a route', A: 'A route' },
  // The CRM nouns read the same everywhere. "Account" is avoided
  // for a company: it already means the tenant and a billing word. A
  // pipeline and its stages are never routing tiers or route groups.
  company: { one: 'company', many: 'companies', One: 'Company', Many: 'Companies', a: 'a company', A: 'A company' },
  deal: { one: 'deal', many: 'deals', One: 'Deal', Many: 'Deals', a: 'a deal', A: 'A deal' },
  pipeline: {
    one: 'pipeline', many: 'pipelines', One: 'Pipeline', Many: 'Pipelines', a: 'a pipeline', A: 'A pipeline',
  },
  stage: { one: 'stage', many: 'stages', One: 'Stage', Many: 'Stages', a: 'a stage', A: 'A stage' },
  // Activity nouns read the same everywhere too.
  task: { one: 'task', many: 'tasks', One: 'Task', Many: 'Tasks', a: 'a task', A: 'A task' },
  note: { one: 'note', many: 'notes', One: 'Note', Many: 'Notes', a: 'a note', A: 'A note' },
  call: { one: 'call', many: 'calls', One: 'Call', Many: 'Calls', a: 'a call', A: 'A call' },
  meeting: { one: 'meeting', many: 'meetings', One: 'Meeting', Many: 'Meetings', a: 'a meeting', A: 'A meeting' },
  activity: {
    one: 'activity', many: 'activities', One: 'Activity', Many: 'Activities', a: 'an activity', A: 'An activity',
  },
  // A person groups the rows that came from one human, across every
  // stream. People.
  person: { one: 'person', many: 'people', One: 'Person', Many: 'People', a: 'a person', A: 'A person' },
  // Who works a record, and the people they work with. The same
  // everywhere; "assigned to" is how a sentence says owner.
  team: { one: 'team', many: 'teams', One: 'Team', Many: 'Teams', a: 'a team', A: 'A team' },
  owner: { one: 'owner', many: 'owners', One: 'Owner', Many: 'Owners', a: 'an owner', A: 'An owner' },
  property: {
    one: 'field', many: 'fields', One: 'Field', Many: 'Fields', a: 'a field', A: 'A field',
  },
  // Automations ("workflow" in code and the API): plain words. A run is
  // one automation carried out for one record.
  workflow: {
    one: 'automation', many: 'automations', One: 'Automation', Many: 'Automations', a: 'an automation', A: 'An automation',
  },
  run: { one: 'run', many: 'runs', One: 'Run', Many: 'Runs', a: 'a run', A: 'A run' },
  phrases: {
    routing: 'Routing',
    routingLower: 'routing',
    reports: 'Analytics',
    sourceTag: 'Source tag',
    sourceTagLower: 'source tag',
    sourceTagMany: 'Source tags',
    sourceTagManyLower: 'source tags',
    // A sale is a sale. "Accepted" stays the word
    // for a buyer's reply ("accepted when" rules), never for a sale.
    sold: 'sold',
    Sold: 'Sold',
    soldPast: 'sold',
    soldPerfect: 'sold',
    soldPrep: 'to',
    Unsold: 'Unsold',
    leadValue: 'Value per row',
    leadValueLower: 'value per row',
    totalLeadValue: 'total value',
    contactDetails: 'Email and phone',
    roleBuyer: 'Data buyer login',
    roleSupplier: 'Data seller login',
    roleSales: 'Sales',
    dataBuyer: 'Data buyer',
    dataBuyers: 'Data buyers',
    dataBuyerLower: 'data buyer',
    dataSeller: 'Data seller',
    dataSellers: 'Data sellers',
    dataSellerLower: 'data seller',
    url: 'URL',
    urlLower: 'URL',
    pingUrl: 'Ping URL',
    postUrl: 'Post URL',
  },
};
